import React, { useEffect, useRef, useState } from "react";
import { Table } from "react-bootstrap";
import { PagedNavTableProps, ColumnDetails } from "./paged-nav-table.props";
import { HoverTooltip } from "../hovertooltip/hovertooltip";
import OverflowTooltip from "../overflow-tooltip/overflow-tooltip";
import FaIcon from "../fa-icon/fa-icon";
import IconEnum from "../../enums/icon.enum";
import Pagination from "../pagination/pagination";
import "./paged-nav-table.scss";

//Displays table with optional title and pagination
//Displays columns with optional sorting and infoIcon
//Displays table data as text with pagination and optional link and tooltip styling
const PagedNavTable = (props: PagedNavTableProps) => {
  const {
    title,
    description,
    columnDetails,
    tableData,
    emptyTableContent,
    tableHeight,
    minTableHeight,
    maxTableHeight,
    sortColumn,
    sortDirection,
    sortChange,
    totalItemsCount,
    includePagination,
    pageNumber,
    pageSize,
    pageSizeOptions,
    pageSizeChange,
    pageChange,
    className,
    addContainerBox = true
  } = props;

  const [firstColumnMargin, setFirstColumnMargin] = useState("0px");
  const [maxColumnWidth, setMaxColumnWidth] = useState("320px");
  const uniqueTooltipAncestorName = `hovertooltip-visibility-ancestor-${Math.random().toString(36).substring(7)}`;
  const tableRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const table = tableRef.current as HTMLElement;
      const firstColumnCells = table.querySelectorAll(".first-column");

      if (table.scrollLeft > 0) {
        firstColumnCells.forEach((cell) => {
          cell.classList.add("scroll-shadow");
        });
      } else {
        firstColumnCells.forEach((cell) => {
          cell.classList.remove("scroll-shadow");
        });
      }
    };

    if (tableRef.current) {
      const table = tableRef.current as HTMLElement;

      const updateColumnWidth = () => {
        requestAnimationFrame(() => {
          const firstColumnWidth = table.querySelector(".first-column")?.getBoundingClientRect().width;
          if (firstColumnWidth) {
            setFirstColumnMargin(`-${firstColumnWidth}px`);
          }

          const evenColumnWidth = table.clientWidth / columnDetails.length;
          //If there's room, allow column width to increase and fill evenly
          //account for 16px padding on each side of table data and 1px for rounded clientwidth
          if (evenColumnWidth > 353) {
            setMaxColumnWidth(`${evenColumnWidth - 33}px`);
          } else {
            setMaxColumnWidth("320px");
          }
        });
      };
      const resizeObserver = new ResizeObserver(updateColumnWidth);
      resizeObserver.observe(table);
      table.addEventListener("scroll", handleScroll);

      return () => {
        table.removeEventListener("scroll", handleScroll);
        resizeObserver.disconnect();
      };
    }
  }, [tableData, columnDetails.length]);

  const displaySortIcon = (column: ColumnDetails) => {
    let sortIcon = <FaIcon icon={IconEnum.SORT} className="sortIconDefault" />;
    if (column.sortProperty === sortColumn) {
      if (sortDirection === "asc") {
        sortIcon = <FaIcon icon={IconEnum.SORT_UP} className="sortIconUp" />;
      } else {
        sortIcon = <FaIcon icon={IconEnum.SORT_DOWN} className="sortIconDown" />;
      }
    }

    return <button onClick={() => sortChange(column.sortProperty)}>{sortIcon}</button>;
  };

  const getIconPadding = (column: ColumnDetails) => {
    if (column.alignment !== "right") {
      return "";
    } else if (column.sortProperty && column.infoIconContent) {
      return "31px";
    } else if (column.sortProperty) {
      return "13px";
    } else if (column.infoIconContent) {
      return "18px";
    } else {
      return "";
    }
  };

  const renderDataOrLink = (column: ColumnDetails, data: any) => {
    const cellStyle = { paddingRight: getIconPadding(column), maxWidth: maxColumnWidth };
    const content = column.getColumnData(data) || "-";
    return column.onLinkSelect && content !== "-" ? (
      <button className="table-cell-link" style={cellStyle} onClick={() => column.onLinkSelect(data)}>
        {content}
      </button>
    ) : (
      <div className="table-cell" style={cellStyle}>
        {content}
      </div>
    );
  };

  const renderCellData = (column: ColumnDetails, data: any, index: number) => {
    const tooltipProps = {
      className: "paged-nav-table-tooltip",
      visibilityMargin: index === 0 ? "0px" : `-40px 0px 0px ${firstColumnMargin}`,
      visibilityAncestorClassname: uniqueTooltipAncestorName,
      help: column.getTooltipContent ? column.getTooltipContent(data) : column.getColumnData(data),
      ignoreOverflow: !!column.getTooltipContent
    };

    return (
      <div className="flex-cell" style={{ justifyContent: column.alignment }}>
        <OverflowTooltip {...tooltipProps}>{renderDataOrLink(column, data)}</OverflowTooltip>
      </div>
    );
  };

  const getColumnStyle = () => {
    if (maxColumnWidth === "320px") {
      //using default max-width, don't override column width to allow overflow
      return {};
    } else {
      //using even max-width, override column width for even distribution
      return { width: `${100 / columnDetails.length}%` };
    }
  };

  const getFirstColClass = (index: number) => (index === 0 ? "first-column" : "");
  const getSortedClass = (column: ColumnDetails) => {
    return column.sortProperty && column.sortProperty === sortColumn ? "sorted" : "";
  };
  const getContainerClass = () => {
    let containerClass = "paged-nav-table-container";
    containerClass += addContainerBox ? " container-box" : "";
    containerClass += className ? ` ${className}` : "";
    containerClass += props.mpngStyle ? " mpng-style" : "";
    return containerClass;
  };

  return (
    <div
      className={getContainerClass()}
      data-testid="paged-nav-table-container"
      style={{
        height: tableHeight || "auto",
        minHeight: minTableHeight || "auto",
        maxHeight: maxTableHeight || "none"
      }}
    >
      {(title || description) && (
        <div className="paged-nav-table-heading">
          {title && <div className="paged-nav-table-title">{title}</div>}
          {description && <div className="paged-nav-table-description">{description}</div>}
        </div>
      )}
      <div ref={tableRef} className={`paged-nav-table ${uniqueTooltipAncestorName}`}>
        <Table className="paged-nav-table-content">
          <thead>
            <tr className="paged-nav-table-header">
              {columnDetails.map((column, index) => (
                <th key={index} className={getFirstColClass(index)} style={getColumnStyle()}>
                  <div
                    className={`header-cell ${getSortedClass(column)}`}
                    style={{ justifyContent: column.alignment, maxWidth: maxColumnWidth }}
                  >
                    {column.name}
                    {column.sortProperty && displaySortIcon(column)}
                    {column.infoIconContent && (
                      <HoverTooltip
                        className="paged-nav-table-tooltip"
                        help={column.infoIconContent}
                        visibilityMargin={index === 0 ? "0px" : `0px 0px 0px ${firstColumnMargin}`}
                        visibilityAncestorClassname={uniqueTooltipAncestorName}
                      >
                        <FaIcon className="column-info-icon" icon={IconEnum.CIRCLE_INFO} />
                      </HoverTooltip>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.map((data, index) => (
              <tr className="paged-nav-table-data-row" key={index}>
                {props.columnDetails.map((column, index) => (
                  <td key={index} className={getFirstColClass(index)} style={getColumnStyle()}>
                    {renderCellData(column, data, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
        {tableData.length === 0 && emptyTableContent}
      </div>
      {includePagination && (
        <div className="pagination-container">
          <Pagination
            itemsCount={totalItemsCount}
            activePage={pageNumber}
            pageSize={pageSize}
            onPageChange={pageChange}
            onPageSizeChange={pageSizeChange}
            showPageSizeChange={true}
            pageSizeOptions={pageSizeOptions}
            mpngStyle={props.mpngStyle}
          />
        </div>
      )}
    </div>
  );
};
export default PagedNavTable;
